import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  analytics as analyticsApi,
  type AnalyticsFilters,
  type AnalyticsRange,
  type AnalyticsSummary,
} from '@/lib/api';
import { countryFlag, countryName } from '@/lib/countries';
import { DashboardLayout } from '@/components/DashboardLayout';
import { LiveNowCard } from '@/components/LiveNowCard';
import { MetricCard } from '@/components/MetricCard';
import { TodayVsYesterdayCard } from '@/components/TodayVsYesterdayCard';
import { Badge, Button, Surface } from '@/components/ui';

const RANGES: Array<{ label: string; value: AnalyticsRange }> = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
  { label: '90 days', value: '90d' },
];

const RANGE_HINT: Record<AnalyticsRange, string> = {
  today: 'Today (UTC, hourly)',
  yesterday: 'Yesterday (UTC, hourly)',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};

// Filter dimensions we mirror to the URL. Order is the canonical pill order.
const FILTER_KEYS = ['country', 'device', 'hostApp', 'browser', 'link'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

export default function Analytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState<AnalyticsRange>('7d');
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  /** True while a filter/range-driven fetch is in flight AND we already
   *  have stale data showing — used to dim the page softly instead of
   *  blanking it. Distinct from `refreshing`, which is the manual "↻"
   *  button's busy state. */
  const [updating, setUpdating] = useState(false);
  const [tick, setTick] = useState(0); // forces "X sec ago" to recompute

  // Derive filters from URL. Memoize a stable serialized form so effect
  // deps don't re-run on every render.
  const filters = useMemo<AnalyticsFilters>(() => {
    const f: AnalyticsFilters = {};
    for (const k of FILTER_KEYS) {
      const v = searchParams.get(k);
      if (v) f[k] = v;
    }
    return f;
  }, [searchParams]);
  const filterKey = useMemo(
    () => FILTER_KEYS.map((k) => `${k}=${filters[k] || ''}`).join('|'),
    [filters],
  );

  // Initial + on-range/filter-change load (KV-cached, ~10ms warm).
  //
  // We deliberately DON'T clear `data` here — keeping the previous
  // breakdown visible while a new fetch is in flight prevents the
  // skeleton-flash on every filter toggle. `cancelled` guards against
  // a fast click sequence applying stale responses out of order.
  useEffect(() => {
    let cancelled = false;
    if (data) setUpdating(true);
    analyticsApi
      .summary(range, false, filters)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setErr(null);
        setUpdating(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e.message || 'Failed to load analytics.');
        setUpdating(false);
      });
    return () => {
      cancelled = true;
    };
    // filterKey collapses the filters object into a stable dep so this only
    // re-fires when filters actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, filterKey]);

  // Auto-refresh every 30 seconds (matches the KV TTL — cheap and feels live).
  useEffect(() => {
    const t = setInterval(() => {
      analyticsApi.summary(range, false, filters).then(setData).catch(() => {});
    }, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, filterKey]);

  // Tick the "X sec ago" indicator every second.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  async function manualRefresh() {
    setRefreshing(true);
    try {
      const fresh = await analyticsApi.summary(range, true, filters);
      setData(fresh);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  }

  function applyFilter(key: FilterKey, value: string) {
    const next = new URLSearchParams(searchParams);
    // Toggle: clicking an already-active row clears that filter.
    if (next.get(key) === value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  }

  function removeFilter(key: FilterKey) {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: true });
  }

  function clearAll() {
    const next = new URLSearchParams(searchParams);
    for (const k of FILTER_KEYS) next.delete(k);
    setSearchParams(next, { replace: true });
  }

  const ageSec = data ? Math.max(0, Math.floor((Date.now() - new Date(data.asOf).getTime()) / 1000)) : 0;
  void tick;

  const titleAction = (
    <div className="flex flex-wrap items-center gap-2">
      {/* Live Now used to live here as a tiny chip — it's now its own
          stat card below. Keeping just the Live · X ago indicator + range
          picker in the header. */}
      {data && (
        <Badge tone="secondary">
          <span
            aria-hidden
            className={`mr-0.5 inline-block h-1.5 w-1.5 rounded-full ${
              updating ? 'animate-ping bg-[#cdbdff]' : 'animate-pulse bg-[#7dffa2]'
            }`}
          />
          {updating ? 'Updating…' : `Live · ${formatAge(ageSec)} ago`}
        </Badge>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={manualRefresh}
        disabled={refreshing}
        title="Force fresh data from Analytics Engine (bypasses 30s cache)"
      >
        {refreshing ? 'Refreshing…' : '↻ Refresh'}
      </Button>
      <div className="flex gap-1 rounded-full border border-[var(--color-border)] bg-[var(--surface-deep)] p-1 text-xs">
        {RANGES.map((r) => (
          <button
            key={r.value}
            onClick={() => setRange(r.value)}
            className={`rounded-full px-3 py-1 transition ${
              r.value === range
                ? 'bg-[var(--color-primary)] font-semibold text-[var(--color-primary-foreground)]'
                : 'text-[var(--color-on-surface-variant)] hover:text-[var(--color-foreground)]'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );

  // -- Build pills from the URL so they appear INSTANTLY on click — the
  //    server's appliedFilters echo arrives a request later, which would
  //    make the pill lag behind the click. For country/device/hostApp/
  //    browser, the label is a pure client-side mapping so URL is enough.
  //    For `link` (UUID → "host/shortCode"), we look up the label in the
  //    last-loaded byLink, then fall back to the server's appliedFilters
  //    echo, then to the raw ID as a final safety net.
  const pills: Array<{ key: FilterKey; icon: string; label: string }> = [];
  if (filters.country) {
    pills.push({
      key: 'country',
      icon: countryFlag(filters.country),
      label: countryName(filters.country),
    });
  }
  if (filters.device) {
    pills.push({ key: 'device', icon: '📱', label: prettyDevice(filters.device) });
  }
  if (filters.hostApp) {
    pills.push({ key: 'hostApp', icon: '📲', label: prettyHostApp(filters.hostApp) });
  }
  if (filters.browser) {
    pills.push({ key: 'browser', icon: '🌐', label: prettyBrowser(filters.browser) });
  }
  if (filters.link) {
    const fromByLink = data?.byLink.find((l) => l.linkId === filters.link)?.label;
    const fromEcho = data?.appliedFilters.link?.label;
    pills.push({
      key: 'link',
      icon: '🔗',
      label: fromByLink || fromEcho || filters.link,
    });
  }

  return (
    <DashboardLayout title="Analytics" titleAction={titleAction}>
      {err && <p className="mb-4 text-sm text-[var(--color-destructive)]">{err}</p>}

      {pills.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-[0.08em] text-[var(--color-on-surface-variant)]">
            Filters
          </span>
          {pills.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => removeFilter(p.key)}
              className="group inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--surface-glass)] py-1 pl-3 pr-2 text-xs font-medium text-[var(--color-foreground)] transition hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10"
              title={`Remove ${p.label} filter`}
            >
              <span aria-hidden>{p.icon}</span>
              <span className="max-w-[200px] truncate">{p.label}</span>
              <span
                aria-hidden
                className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-[var(--color-on-surface-variant)] transition group-hover:bg-[var(--color-primary)]/20 group-hover:text-[var(--color-foreground)]"
              >
                ×
              </span>
            </button>
          ))}
          {pills.length >= 2 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs font-medium text-[var(--color-on-surface-variant)] underline-offset-4 hover:text-[var(--color-foreground)] hover:underline"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {!data && !err && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="h-28 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
            <div className="h-28 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
            <div className="h-28 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
          </div>
          <div className="h-72 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
        </div>
      )}

      {data && (
        <div
          className={`transition-opacity duration-200 ${
            updating ? 'opacity-60' : 'opacity-100'
          }`}
          aria-busy={updating}
        >
          {/* 4-card grid — Total Clicks anchors the left as the headline
              for the selected range; Live Now sits on the right where the
              eye scans last (it's the "what's happening right now" beat). */}
          <div className="mb-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              label="Total clicks"
              value={data.totalClicks.toLocaleString()}
              meta={RANGE_HINT[data.range] ?? `Last ${data.days} days`}
            />
            <TodayVsYesterdayCard delta={data.delta} />
            <MetricCard
              label="Top country"
              value={data.byCountry[0] ? countryName(data.byCountry[0].country) : '—'}
              meta={
                data.byCountry[0]
                  ? `${data.byCountry[0].clicks.toLocaleString()} clicks`
                  : 'No data yet'
              }
            />
            <LiveNowCard liveNow={data.liveNow} />
          </div>

          <Card title="Clicks over time">
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <AreaChart
                  data={data.byDay.map((d) => ({
                    // Hour buckets come back as "YYYY-MM-DD HH:00" — strip to
                    // "HH:00". Day buckets are "YYYY-MM-DD" — strip to "MM-DD".
                    day:
                      data.granularity === 'hour'
                        ? d.day.slice(11, 16)
                        : d.day.slice(5),
                    clicks: d.clicks,
                  }))}
                  margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient id="clicksGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#cdbdff" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#cdbdff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="day" stroke="var(--color-on-surface-variant)" fontSize={12} />
                  <YAxis stroke="var(--color-on-surface-variant)" fontSize={12} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: 'rgba(20, 20, 20, 0.85)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 12,
                      fontSize: 12,
                      boxShadow: '0 12px 32px -16px rgba(0,0,0,0.6)',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="clicks"
                    stroke="#cdbdff"
                    strokeWidth={2.5}
                    fill="url(#clicksGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="mt-5">
            <Card title="Top traffic links">
              <BreakdownTable
                rows={data.byLink.map((l) => ({
                  label: l.label,
                  clicks: l.clicks,
                  activeValue: l.linkId,
                }))}
                activeValue={data.appliedFilters.link?.id}
                onRowClick={(row) => applyFilter('link', row.activeValue!)}
                emptyHint="No link data yet"
                wideLabel
              />
            </Card>
          </div>

          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <Card title="By country">
              <BreakdownTable
                rows={data.byCountry.map((c) => ({
                  label: countryName(c.country),
                  clicks: c.clicks,
                  badge:
                    c.country.toUpperCase() === '--'
                      ? undefined
                      : countryFlag(c.country),
                  activeValue: c.country.toUpperCase(),
                }))}
                activeValue={data.appliedFilters.country}
                onRowClick={(row) => applyFilter('country', row.activeValue!)}
                emptyHint="No country data yet"
              />
            </Card>
            <Card title="By source app">
              <BreakdownTable
                rows={data.byHostApp.map((h) => ({
                  label: prettyHostApp(h.hostApp),
                  clicks: h.clicks,
                  activeValue: h.hostApp,
                }))}
                activeValue={data.appliedFilters.hostApp}
                onRowClick={(row) => applyFilter('hostApp', row.activeValue!)}
                emptyHint="No source data yet"
              />
            </Card>
          </div>

          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <Card title="By device">
              <BreakdownTable
                rows={data.byDevice.map((d) => ({
                  label: prettyDevice(d.device),
                  clicks: d.clicks,
                  activeValue: d.device,
                }))}
                activeValue={data.appliedFilters.device}
                onRowClick={(row) => applyFilter('device', row.activeValue!)}
                emptyHint="No device data yet"
              />
            </Card>
            <Card title="By browser">
              <BreakdownTable
                rows={data.byBrowser.map((b) => ({
                  label: prettyBrowser(b.browser),
                  clicks: b.clicks,
                  activeValue: b.browser,
                }))}
                activeValue={data.appliedFilters.browser}
                onRowClick={(row) => applyFilter('browser', row.activeValue!)}
                emptyHint="No browser data yet"
              />
            </Card>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Surface tone="raised" className="p-6">
      <div className="mb-4 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-on-surface-variant)]">
        {title}
      </div>
      {children}
    </Surface>
  );
}

interface BreakdownRow {
  label: string;
  clicks: number;
  /** Optional left-of-label glyph — either a short text code (rendered
   *  in a monospace chip) or a single emoji (rendered bare). The
   *  component auto-detects which based on character class. */
  badge?: string;
  /** Opaque value that identifies the row for filtering. The parent
   *  passes whichever ID is appropriate for the dimension (country code,
   *  hostApp slug, link UUID, …) — BreakdownTable doesn't interpret it. */
  activeValue?: string;
}

function BreakdownTable({
  rows,
  emptyHint,
  onRowClick,
  activeValue,
  wideLabel = false,
}: {
  rows: BreakdownRow[];
  emptyHint: string;
  onRowClick?: (row: BreakdownRow) => void;
  /** The currently-active filter value for this dimension. Matching rows
   *  render as pressed; clicking again clears the filter (toggle). */
  activeValue?: string;
  /** Set on full-width cards that carry long labels (URLs). Doubles the
   *  label column and caps the bar so it doesn't stretch across the whole
   *  page. */
  wideLabel?: boolean;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-[var(--color-on-surface-variant)]">{emptyHint}</p>;
  }
  const max = Math.max(...rows.map((r) => r.clicks), 1);
  // Total computed across ALL rows in the data (not just the visible
  // top-10 slice) so percentages are honest — a row showing "12.3%"
  // means 12.3% of this dimension's total, not 12.3% of the top-10.
  const total = rows.reduce((s, r) => s + r.clicks, 0);
  const clickable = !!onRowClick;
  const labelColCls = wideLabel
    ? 'flex min-w-0 basis-72 shrink-0 items-center gap-2 font-medium'
    : 'flex w-32 min-w-0 items-center gap-1.5 truncate font-medium';
  // Bar wrapper: on wide-label rows we cap the bar so the top-row doesn't
  // run page-wide; on normal rows we let it stretch within the card.
  const barWrapCls = wideLabel ? 'relative flex-1 max-w-md' : 'relative flex-1';
  return (
    <ul className="flex flex-col gap-1">
      {rows.slice(0, 10).map((r, i) => {
        const isActive = !!activeValue && r.activeValue === activeValue;
        // Emoji badges are single grapheme clusters of non-ASCII; render
        // bare. Text codes (e.g. "AR") get the monospace chip treatment.
        const badgeIsEmoji = !!r.badge && !/^[A-Za-z0-9_-]+$/.test(r.badge);
        const inner = (
          <>
            <div className={labelColCls}>
              {r.badge && badgeIsEmoji && (
                <span className="text-base leading-none" aria-hidden>
                  {r.badge}
                </span>
              )}
              {r.badge && !badgeIsEmoji && (
                <span className="rounded bg-[var(--color-surface-container)] px-1 py-0.5 font-mono text-[10px] text-[var(--color-on-surface-variant)]">
                  {r.badge}
                </span>
              )}
              <span className="truncate" title={r.label}>
                {r.label}
              </span>
            </div>
            <div className={barWrapCls}>
              <div
                className="h-2 rounded-full"
                style={{
                  width: `${(r.clicks / max) * 100}%`,
                  background: 'linear-gradient(90deg, #cdbdff 0%, #7c4dff 100%)',
                }}
              />
            </div>
            <div className="w-16 text-right font-mono text-sm tabular-nums text-[var(--color-on-surface-variant)]">
              {r.clicks.toLocaleString()}
            </div>
            <div className="w-16 text-right font-mono text-sm font-medium tabular-nums text-[#16a34a]">
              {formatPct(r.clicks, total)}
            </div>
          </>
        );

        if (clickable) {
          return (
            <li key={`${r.label}-${i}`}>
              <button
                type="button"
                onClick={() => onRowClick!(r)}
                aria-pressed={isActive}
                className={`flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left text-sm transition ${
                  isActive
                    ? 'bg-[var(--color-primary)]/15 ring-1 ring-[var(--color-primary)]/40'
                    : 'hover:bg-[var(--surface-glass)]'
                }`}
                title={isActive ? 'Click to remove filter' : `Filter to ${r.label}`}
              >
                {inner}
              </button>
            </li>
          );
        }

        return (
          <li key={`${r.label}-${i}`} className="flex items-center gap-3 px-2 py-1.5 text-sm">
            {inner}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Format a row's share of the breakdown total. Tiny rows ("<0.1%") get
 * a friendly minimum so a "0.0%" reading doesn't look like a bug — but
 * the actual zero case (total === 0) returns an em-dash.
 */
function formatPct(value: number, total: number): string {
  if (!total) return '—';
  const pct = (value / total) * 100;
  if (pct === 0) return '0%';
  if (pct < 0.1) return '<0.1%';
  if (pct >= 99.95) return '100%';
  return `${pct.toFixed(1)}%`;
}

function formatAge(seconds: number): string {
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}

function prettyHostApp(s: string): string {
  switch (s) {
    case 'instagram':
      return 'Instagram';
    case 'threads':
      return 'Threads';
    case 'facebook':
      return 'Facebook';
    case 'messenger':
      return 'Messenger';
    case 'tiktok':
      return 'TikTok';
    case 'twitter':
      return 'X (Twitter)';
    case 'linkedin':
      return 'LinkedIn';
    case 'pinterest':
      return 'Pinterest';
    case 'reddit':
      return 'Reddit';
    case 'snapchat':
      return 'Snapchat';
    // Legacy slug — no longer written by the bouncer (any unmatched
    // Android UA now returns null → 'unknown'). Historical rows are
    // collapsed into 'unknown' for the source-app panel via
    // mergeLegacyAndroidIntoUnknown, so this case only fires if a
    // stale value somehow reaches a per-row label (pill, filter chip).
    case 'android-webview':
      return 'Android (legacy)';
    case 'unknown':
      return 'Direct / other';
    case '—':
      return s;
    default:
      return s.charAt(0).toUpperCase() + s.slice(1);
  }
}

/** Customer-facing label for the device class stored in blob12. */
function prettyDevice(s: string): string {
  switch (s) {
    case 'mobile':
      return 'Phone';
    case 'tablet':
      return 'Tablet';
    case 'desktop':
      return 'Desktop';
    default:
      return s.charAt(0).toUpperCase() + s.slice(1);
  }
}

/** Customer-facing label for the browser bucket stored in blob11. The
 *  raw values are lowercase ids (chrome / safari / firefox / edge /
 *  samsung / opera / in-app / other) — humanize for display. */
function prettyBrowser(s: string): string {
  switch (s) {
    case 'chrome':
      return 'Chrome';
    case 'safari':
      return 'Safari';
    case 'firefox':
      return 'Firefox';
    case 'edge':
      return 'Edge';
    case 'samsung':
      return 'Samsung Internet';
    case 'opera':
      return 'Opera';
    case 'in-app':
      return 'In-app browser';
    case 'other':
      return 'Other';
    default:
      return s.charAt(0).toUpperCase() + s.slice(1);
  }
}
